import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowDown, Droplet } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { Reveal, MaskLine } from "../components/Reveal";
import emblem from "../assets/banner.png";
import makingImg from "../assets/making-softbaits.png";
import readWaterImg from "../assets/read-the-water.png";
import typesImg from "../assets/types-03.png";
import Intro from "../components/Intro";
import { VideoHero } from "../components/VideoHero";

const HERO_IMG =
  "https://images.unsplash.com/photo-1484387436194-cf7cb70800ce?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const CHAPTERS = [
  {
    n: "01",
    title: "Poured, not printed",
    body: "Every bait is small-batch hand-poured with premium plastisol and tuned for a lifelike fall. No mass production. No compromise.",
    img: makingImg,
  },
  {
    n: "02",
    title: "Read the water",
    body: "Fish don't see a color name. They see profile, movement, contrast and opportunity. Drift colors and profiles are developed around real forage and real water—from stained backwaters to gin-clear current. Match the water. Match the forage. Earn the bite.",
    img: readWaterImg,
  },
  {
    n: "03",
    title: "Field tested",
    body: "Proven on pressured tournament water before it ever reaches your tackle box. If we wouldn't fish it, we won't sell it.",
    img: typesImg,
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [catches, setCatches] = useState([]);
  const [reveal, setReveal] = useState(() => !!sessionStorage.getItem("drift_intro_seen"));
  const [showIntro] = useState(() => !sessionStorage.getItem("drift_intro_seen"));
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 13))).catch(() => {});
    api.get("/testimonials").then((r) => setCatches(r.data.filter((t) => t.image).slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div className="bg-[#04070A] min-h-screen">
      {showIntro && (
        <Intro
          onDone={() => {
            sessionStorage.setItem("drift_intro_seen", "1");
            setReveal(true);
          }}
        />
      )}
      <Nav />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-end overflow-hidden">
        <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="Dark water" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#04070A] via-[#04070A]/50 to-[#04070A]/30" />
        </motion.div>

        <motion.div style={{ opacity }} className="relative z-10 max-w-[1600px] w-full mx-auto px-6 md:px-10 pb-20 pt-40">
          <MaskLine delay={0.1} play={reveal}>
            <span className="font-mono-plex text-xs tracking-[0.3em] uppercase text-orange-500">
              Hand-poured soft plastics
            </span>
          </MaskLine>
          <h1 className="font-display font-semibold uppercase tracking-tighter leading-[0.82] text-6xl sm:text-8xl lg:text-[9vw] mt-6">
            <MaskLine delay={0.2} play={reveal}>Baits that</MaskLine>
            <MaskLine delay={0.35} play={reveal}>
              <span className="text-orange-500">swim</span> like the
            </MaskLine>
            <MaskLine delay={0.5} play={reveal}>real thing.</MaskLine>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={reveal ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6"
          >
            <Link
              to="/shop"
              className="group inline-flex items-center gap-3 bg-orange-500 text-[#04070A] font-mono-plex text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-orange-600 active:scale-95 transition-all w-fit"
              data-testid="hero-shop-button"
            >
              Shop the Range
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <p className="max-w-sm text-slate-400 text-sm leading-relaxed">
              Small-batch soft baits engineered by anglers, for anglers. Field-tested on the water you fish.
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={reveal ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 1.1 }}
          className="absolute bottom-8 right-8 z-10 hidden md:flex items-center gap-2 text-slate-500"
        >
          <span className="font-mono-plex text-[10px] tracking-[0.2em] uppercase">Scroll</span>
          <ArrowDown className="w-4 h-4 animate-bounce" />
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/5 py-6 bg-[#04070A]">
        <Marquee speed={40} gradient={false}>
          {["Precision Engineered Softbaits", "Field Tested", "Hand Poured", "Water Proven", "Small Batch"].map((t, i) => (
            <span key={i} className="font-display text-3xl md:text-5xl uppercase tracking-tight text-slate-700 mx-16 flex items-center gap-16">
              {t} <span className="w-2 h-2 bg-orange-500 rotate-45 inline-block" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* PRODUCT LAUNCH VIDEO */}
      <VideoHero
        src="https://customer-assets-agu9un31.emergentagent.net/wingman/d460406f-d695-4400-89bd-7a845381c1d4/attachments/8bac216110cb4ef6a875f1030e7e6442_drift-launch-60s-v2.mp4"
        label="The Launch Film"
        title="Drift Tackle Co. — Launch Film"
      />

      {/* STORY + FEATURED RAIL */}
      <section id="story" className="bg-[#0A1118] border-y border-white/5 py-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex items-start justify-between gap-6 mb-16">
              <div className="max-w-4xl">
                <span className="font-mono-plex text-xs tracking-[0.25em] uppercase text-orange-500">The Drift Standard</span>
                <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl uppercase tracking-tighter mt-4 leading-[0.9]">
                  We believe there are still better baits to be made.
                </h2>
                <p className="text-slate-400 text-base md:text-lg leading-relaxed mt-8 max-w-2xl">
                  We aren&rsquo;t here to make another version of what already exists. We build with purpose—studying movement, profile, presentation and the way fish actually respond.
                </p>
                <p className="text-slate-200 text-base md:text-lg leading-relaxed mt-4 max-w-2xl">
                  Every design has to earn its place. And every bait has to earn the next cast.
                </p>
              </div>
              <img src={emblem} alt="Drift Tackle Co. emblem" className="hidden lg:block h-40 w-40 object-contain mix-blend-screen shrink-0 -mt-4" />
            </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* LEFT 2/3 — numbered content */}
            <div className="lg:col-span-8 flex flex-col gap-20">
              {CHAPTERS.map((c) => (
                <div key={c.n}>
                  <Reveal>
                    <div className="relative overflow-hidden border border-white/5 aspect-[3/2]">
                      <img src={c.img} alt={c.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  </Reveal>
                  <Reveal delay={0.1}>
                    <div className="mt-6 flex gap-5 md:gap-6 items-start">
                      <span className="font-mono-plex text-4xl md:text-6xl text-white/10 font-medium leading-none shrink-0">{c.n}</span>
                      <div>
                        <h3 className="font-display text-2xl md:text-4xl uppercase tracking-tight">{c.title}</h3>
                        <p className="text-slate-400 text-lg leading-relaxed mt-4 max-w-2xl">{c.body}</p>
                      </div>
                    </div>
                  </Reveal>
                </div>
              ))}
            </div>

            {/* RIGHT 1/3 — compact featured showcase */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <div className="flex items-center justify-between mb-6">
                  <span className="font-mono-plex text-xs tracking-[0.25em] uppercase text-orange-500">Best Sellers</span>
                  <Link to="/shop" className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-400 hover-underline pb-1" data-testid="featured-view-all">All →</Link>
                </div>
                <div className="flex flex-col divide-y divide-white/5 border-y border-white/5">
                  {featured.map((p) => (
                    <Link key={p.id} to={`/product/${p.id}`} className="group flex items-center gap-4 py-4" data-testid={`featured-item-${p.id}`}>
                      <div className="w-16 h-16 shrink-0 overflow-hidden bg-[#04070A] border border-white/5">
                        <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display uppercase text-sm tracking-tight truncate group-hover:text-orange-400 transition-colors">{p.name}</p>
                        <p className="font-mono-plex text-[10px] text-slate-500 tracking-wide">{p.category}</p>
                      </div>
                      <span className="font-mono-plex text-sm text-orange-500 shrink-0">${p.price?.toFixed(2)}</span>
                    </Link>
                  ))}
                </div>
                <Link
                  to="/shop"
                  className="mt-6 flex items-center justify-center gap-2 border border-white/15 font-mono-plex text-[10px] tracking-[0.2em] uppercase py-3 text-slate-300 hover:border-orange-500/50 hover:text-orange-400 transition-colors"
                  data-testid="featured-shop-all"
                >
                  Shop All Baits
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* JOIN THE CLUB BANNER */}
      <section className="bg-[#04070A] py-20 md:py-28" data-testid="club-banner">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="relative overflow-hidden bg-orange-500 text-[#04070A]">
              {/* faint droplet motif */}
              <Droplet className="absolute -right-8 -bottom-10 w-72 h-72 text-[#04070A]/10 rotate-12 pointer-events-none" />
              <div className="relative grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center p-8 sm:p-12 md:p-16">
                <div>
                  <span className="font-mono-plex text-xs tracking-[0.3em] uppercase text-[#04070A]/70">
                    Drift Tackle Club
                  </span>
                  <h2 className="font-display text-4xl sm:text-6xl uppercase tracking-tighter mt-4 leading-[0.9]">
                    A new color,
                    <br />
                    every single month.
                  </h2>
                  <p className="text-[#04070A]/80 text-base sm:text-lg leading-relaxed mt-6 max-w-xl">
                    Members get our exclusive Color of the Month and fresh-poured samples shipped
                    straight to the door — from just <span className="font-semibold">$9.99/mo</span>. Limited drops, cancel anytime.
                  </p>
                </div>
                <div className="lg:justify-self-end">
                  <Link
                    to="/club"
                    className="group inline-flex items-center gap-3 bg-[#04070A] text-orange-500 font-mono-plex text-xs tracking-[0.2em] uppercase px-8 py-5 hover:bg-[#0A1118] active:scale-95 transition-all w-fit"
                    data-testid="club-banner-cta"
                  >
                    Join the Club
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* GALLERY PLUG — compact, only when there are approved catches */}
      {catches.length > 0 && (
        <section className="bg-[#04070A] pb-4" data-testid="gallery-plug">
          <div className="max-w-[1600px] mx-auto px-6 md:px-10">
            <Reveal>
              <div className="border border-white/5 bg-[#0A1118] p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
                <div className="md:flex-1">
                  <span className="font-mono-plex text-[11px] tracking-[0.25em] uppercase text-orange-500">The Wall</span>
                  <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight mt-2">Real catches on Drift baits</h3>
                  <Link
                    to="/testimonials"
                    className="group inline-flex items-center gap-2 mt-3 font-mono-plex text-[11px] tracking-[0.2em] uppercase text-slate-300 hover:text-orange-400 transition-colors"
                    data-testid="gallery-plug-link"
                  >
                    See the gallery
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
                <div className="flex gap-3">
                  {catches.map((t) => (
                    <Link key={t.id} to="/testimonials" className="w-20 h-20 md:w-24 md:h-24 shrink-0 overflow-hidden border border-white/5 group">
                      <img src={fileUrl(t.image)} alt={t.name || "Angler catch"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* BRAND STORY */}
      <section className="bg-[#04070A] py-24 md:py-32">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <Reveal className="flex flex-col items-center text-center">
            <img
              src={emblem}
              alt="Drift Tackle Co. — Built in the North. Proven everywhere."
              className="h-32 w-32 sm:h-40 sm:w-40 object-contain mix-blend-screen mb-8"
            />
            <span className="font-mono-plex text-xs tracking-[0.25em] uppercase text-orange-500">Built in the North. Proven everywhere.</span>
            <h2 className="font-display text-4xl sm:text-6xl uppercase tracking-tighter mt-4 max-w-3xl leading-[0.9]">
              Born from a lifetime on the water
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="max-w-2xl mx-auto text-center mt-8 space-y-5">
              <p className="text-slate-300 text-lg leading-relaxed">
                Drift started with a simple belief: there are still great baits left to be made.
              </p>
              <p className="text-slate-400 text-base leading-relaxed">
                Not more of the same. Not products designed around hype. Baits designed around movement, presentation and what fish actually respond to.
              </p>
              <p className="text-slate-200 text-base leading-relaxed">
                We&rsquo;re building Drift one bait, one fisherman and one cast at a time.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
